import { autoinject } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { localSort } from 'app/shared/lib/sorting';
import { SelectEvent } from 'app/shared/elements/ess-table';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { Api } from 'app/shared/lib/essette-api';
import { guid } from 'app/shared/lib/guid';
import { I18N } from 'aurelia-i18n';

interface IBrick {
    id: number;
    description: string;
    colors?: IColor[];
    selectable?: boolean;
    expandable?: boolean;
}

interface IColor {
    color: string;
}

@autoinject()
export class DemoEssTablePage extends ComponentBase {
    ////****************** Fields and Properties ************************************////
    protected sorting = new Sorting();
    protected defaultSorting = new Sorting();
    protected id: guid = guid();

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        protected readonly api: Api,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);

        this.defaultSorting.entries = [
            {
                reference: 'id',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Functions ************************************************////
    protected async reload(): Promise<void> {
        await this.loadBricks();
    }

    protected applySort(event: SortChangeEvent, items: IBrick[], updateEx4: boolean = false): void {
        const { property, direction } = event.detail;
        const { sorting, defaultSorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        // if no sorting entries specified, use default sorting
        localSort(items, sorting.entries.length > 0 ? sorting : defaultSorting);
        if(updateEx4) this.ex4UpdateDisplayedBricks();
    }

    protected async loadBricks(): Promise<void> {
        try {
            this.state.beginLoad();
            const response = await this.api.mockGet('bricks');
            const allBricks = response.results;
            // distribute bricks to each example
            this.ex1Bricks = allBricks.slice(0, 5);
            this.ex2Bricks = allBricks.slice(5, 10);
            // for ex3 we add a selectable property to all but one brick
            this.ex3Bricks = allBricks.slice(10, 15).map((brick: IBrick, index: number) => { return index === 1 ? brick : { ...brick, selectable: true } }
            );
            // ex4 uses local paging
            this.ex4Bricks = allBricks.slice(15, 25);
            this.ex4UpdateDisplayedBricks();
            // for ex5 we add an expandable property and details data to all but one brick
            const colors: IColor[][] = [
                [{ color: 'Light Green' }, { color: 'Yellow' }, { color: 'Blue' }],
                [],
                [{ color: 'Dark Brown' }, { color: 'Forest Green' }],
                [{ color: 'Icy Blue' }, { color: 'Mauve' }],
                [{ color: 'Burnt Sienna' }, { color: 'White' }, { color: 'Magenta' }],
            ];
            this.ex5Bricks = allBricks.slice(25, 30).map((brick: IBrick, index: number) => { return index === 1 ? brick : { ...brick, expandable: true, colors: colors[index] } });
            this.ex6Bricks = allBricks.slice(0, 5);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    ////****************** Example 1 ************************************************////
    protected ex1Bricks: IBrick[] = [];

    ////****************** Example 2 ************************************************////
    protected ex2Bricks: IBrick[] = [];
    protected ex2Configurable: boolean = true;

    ////****************** Example 3 ************************************************////
    protected ex3Bricks: IBrick[] = [];
    protected ex3SelectedBricks: IBrick[] = [];
    protected ex3Selectable: boolean = true;
    protected ex3RowSelectable: string = 'selectable';
    protected ex3SelectRowTooltip: string = 'Select';
    protected ex3SelectAllTooltip: string = 'Select Items';
    protected ex3ID: guid = guid();

    protected ex3BricksSelected(event: SelectEvent<IBrick>): void {
        this.ex3SelectedBricks = [...event.detail.items.filter(brick => brick.selectable)];
    }

    ////****************** Example 4 ************************************************////
    protected $pager!: EssPagerCustomElement;
    protected ex4Bricks: IBrick[] = [];
    protected ex4DisplayedBricks: IBrick[] = [];
    protected ex4SelectedBricks: IBrick[] = [];
    protected ex4Selectable: boolean = true;
    protected ex4PageSize: number = 5;
    protected ex4Paged: boolean = true;
    protected ex4SelectAllTooltip: string = 'Select Items';
    protected ex4ID: guid = guid();

    protected ex4UpdateDisplayedBricks(): void {
        this.ex4DisplayedBricks = this.ex4Bricks.slice(this.$pager.page * this.ex4PageSize, this.$pager.page * this.ex4PageSize + this.ex4PageSize);
    }

    protected ex4BricksSelected(event: SelectEvent<IBrick>): void {
        this.ex4SelectedBricks = [...event.detail.items];
    }

    ////****************** Example 5 ************************************************////
    protected ex5Bricks: IBrick[] = [];
    protected ex5RowExpandable: string = 'expandable';

    ////****************** Example 6 ************************************************////
    protected ex6Bricks: IBrick[] = [];
    protected ex6Scrollable: boolean = true;
    protected ex6CaptionTitle: string = this.i18n.tr('core:demo.tableTitle.$', { what: '8' });
}
